/* ============================================================
   LIORA - Atelier-Story auf Produktseiten
   (sections/liora-pdp-story.liquid)

   - Blendet Bloecke ein, sobald sie ins Bild kommen
   - Zeichnet das Stoff-Motiv und den Groessenvergleich
   - Laesst die Naht im Atelier-Prozess mit dem Scrollen wachsen
   - Markiert die gewaehlte Groesse der Everything Bag
   - Stoff-Lupe: zeigt den Stoff unter dem Mauszeiger vergroessert

   Ohne JavaScript und bei 'Bewegung reduzieren' bleibt alles
   sichtbar und statisch.
   ============================================================ */
if (!customElements.get('liora-story')) {
  customElements.define(
    'liora-story',
    class LioraStory extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        const { signal } = this.controller;

        this.syncSizes();
        document.addEventListener('change', (event) => this.onOptionChange(event), { signal });
        this.setupLoupe(signal);

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion || !('IntersectionObserver' in window)) return;

        this.classList.add('is-armed');
        this.observeReveals();
        this.observeVisibility();
        this.setupSeam(signal);
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.revealObserver?.disconnect();
        this.visibilityObserver?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      observeReveals() {
        const targets = [...this.querySelectorAll('[data-story-reveal], [data-motif]')];

        this.revealObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              const target = entry.target;
              target.classList.add(target.hasAttribute('data-motif') ? 'is-drawn' : 'is-in');
              this.revealObserver.unobserve(target);
            }
          },
          { threshold: 0.18, rootMargin: '0px 0px -8% 0px' }
        );

        for (const target of targets) {
          /* Das Motiv zeichnet sich erst, wenn die Stoffprobe aufgezogen ist. */
          if (target.hasAttribute('data-motif')) {
            target.style.setProperty('--motif-delay', '700ms');
            target.classList.add('motif-armed');
          }
          this.revealObserver.observe(target);
        }
      }

      observeVisibility() {
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.classList.toggle('is-visible', entry.isIntersecting);
        });
        this.visibilityObserver.observe(this);
      }

      setupSeam(signal) {
        this.track = this.querySelector('[data-story-seam]');
        if (!this.track) return;

        this.steps = [...this.track.querySelectorAll('.liora-story__step')];
        this.track.classList.add('is-armed');
        this.track.style.setProperty('--seam', '0');

        const request = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.updateSeam();
          });
        };

        window.addEventListener('scroll', request, { passive: true, signal });
        window.addEventListener('resize', request, { passive: true, signal });
        request();
      }

      updateSeam() {
        const rect = this.track.getBoundingClientRect();
        const viewport = window.innerHeight;
        const start = viewport * 0.9;
        const distance = viewport * 0.2 + rect.height;
        const progress = Math.min(1, Math.max(0, (start - rect.top) / distance));

        this.track.style.setProperty('--seam', progress.toFixed(4));
        this.track.style.setProperty('--seam-done', progress >= 1 ? '1' : '0');

        const vertical = window.matchMedia('(max-width: 989px)').matches;
        this.steps.forEach((step, index) => {
          const position = vertical
            ? (step.offsetTop + 12) / this.track.offsetHeight
            : index / this.steps.length + 0.02;
          step.classList.toggle('is-reached', progress >= position);
        });
      }

      /* Stoff-Lupe (nur Maus): das Stoffbild ist per object-fit: cover
         eingepasst, daher wird der Ausschnitt fuer die Lupe nachgerechnet. */
      setupLoupe(signal) {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        const swatch = this.querySelector('[data-loupe]');
        const image = swatch?.querySelector('img');
        const loupe = swatch?.querySelector('.liora-story__loupe');
        if (!swatch || !image || !loupe) return;

        const zoom = 2.4;

        const move = (event) => {
          const rect = swatch.getBoundingClientRect();
          const x = event.clientX - rect.left;
          const y = event.clientY - rect.top;
          const ratio = image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : 0.75;

          let drawnWidth = rect.width;
          let drawnHeight = rect.width / ratio;
          if (drawnHeight < rect.height) {
            drawnHeight = rect.height;
            drawnWidth = rect.height * ratio;
          }
          const offsetX = (drawnWidth - rect.width) / 2;
          const offsetY = (drawnHeight - rect.height) / 2;
          const radius = loupe.offsetWidth / 2;

          swatch.style.setProperty('--lx', x + 'px');
          swatch.style.setProperty('--ly', y + 'px');
          loupe.style.setProperty('--lsize', drawnWidth * zoom + 'px');
          loupe.style.setProperty('--lbx', -((x + offsetX) * zoom - radius) + 'px');
          loupe.style.setProperty('--lby', -((y + offsetY) * zoom - radius) + 'px');
        };

        swatch.addEventListener(
          'pointerenter',
          (event) => {
            move(event);
            swatch.classList.add('is-loupe');
          },
          { signal }
        );
        swatch.addEventListener('pointermove', move, { signal });
        swatch.addEventListener('pointerleave', () => swatch.classList.remove('is-loupe'), { signal });
      }

      /* Everything Bag: gewaehlte Groesse im Vergleich hervorheben */
      syncSizes() {
        this.sizes = [...this.querySelectorAll('.liora-story__size[data-size]')];
        if (!this.sizes.length) return;

        const checked = document.querySelector(
          'variant-picker input[type="radio"]:checked, .variant-picker input[type="radio"]:checked'
        );
        if (checked) this.highlightSize(checked.value);
      }

      onOptionChange(event) {
        if (!this.sizes?.length) return;
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
        this.highlightSize(input.value);
      }

      highlightSize(value) {
        const match = this.sizes.some((size) => size.dataset.size === value);
        if (!match) return;
        for (const size of this.sizes) size.classList.toggle('is-current', size.dataset.size === value);
      }
    }
  );
}
